import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DashboardApiService } from './dashboard-api.service';
import type { DashboardCompletionItem, DashboardPeriod, DashboardStats } from './dashboard.models';

interface DashboardState {
  period: DashboardPeriod;
  loading: boolean;
  error: string | null;
  data: DashboardStats | null;
}

const initialState: DashboardState = {
  period: '30d',
  loading: false,
  error: null,
  data: null,
};

@Injectable({ providedIn: 'root' })
export class DashboardStore {
  private readonly api = inject(DashboardApiService);

  private state = signal<DashboardState>(initialState);
  private loadRequestId = 0;

  readonly loading = computed(() => this.state().loading);
  readonly error = computed(() => this.state().error);
  readonly period = computed(() => this.state().period);
  readonly data = computed(() => this.state().data);
  readonly hasAnyData = computed(() => !!this.state().data);
  readonly mainCompletions = computed<DashboardCompletionItem[]>(
    () => this.state().data?.main.completions ?? [],
  );
  readonly perspectiveCompletions = computed<DashboardCompletionItem[]>(
    () => this.state().data?.perspective.completions ?? [],
  );
  readonly retrospectiveCompletions = computed<DashboardCompletionItem[]>(
    () => this.state().data?.retrospective.completions ?? [],
  );

  async loadStats(): Promise<void> {
    const period = this.state().period;
    const requestId = ++this.loadRequestId;
    try {
      this.state.update((s) => ({ ...s, loading: true, error: null }));
      const data = await firstValueFrom(this.api.getStats(period));
      if (requestId !== this.loadRequestId) return;
      this.state.update((s) => ({ ...s, data }));
    } catch (e: unknown) {
      if (requestId !== this.loadRequestId) return;
      const msg =
        (e as { error?: { message?: string }; message?: string })?.error?.message ??
        (e as { message?: string })?.message ??
        'Не удалось загрузить статистику';
      this.state.update((s) => ({ ...s, error: msg, data: null }));
      throw e;
    } finally {
      if (requestId === this.loadRequestId) {
        this.state.update((s) => ({ ...s, loading: false }));
      }
    }
  }

  async setPeriod(period: DashboardPeriod): Promise<void> {
    this.state.update((s) => ({ ...s, period, data: null, error: null }));
    await this.loadStats();
  }

  clear(): void {
    this.loadRequestId++;
    this.state.set(initialState);
  }
}
